import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Brain, Users, Sparkles, Target, Loader2, KeyRound } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/roles";
import { AccessCodeForm } from "@/components/AccessCodeForm";

const PILLARS = [
  [Brain, "Cognitive & aptitude", "Reasoning, numeracy and problem-solving"],
  [Users, "DISC personality", "How you work, lead and collaborate"],
  [Sparkles, "Holland interests", "The environments that energise you"],
  [Target, "Career alignment", "Ranked matches and a 90-day plan"],
];

const PartnerPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [partner, setPartner] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => { if (!loading && user) navigate(isAdminRole(user) ? "/admin" : "/dashboard"); }, [user, loading, navigate]);
  useEffect(() => {
    api.get(`/partners/${encodeURIComponent(slug)}`).then((r) => setPartner(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-5" data-testid="partner-not-found">
        <div className="text-center">
          <h1 className="font-heading font-extrabold text-2xl text-slate-900">This partner page doesn't exist</h1>
          <p className="font-body text-slate-500 mt-2">Check the link you were given, or <button onClick={() => navigate("/access")} className="text-primary font-semibold underline">enter your code here</button>.</p>
        </div>
      </div>
    );
  }
  if (!partner) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-[#0B1020] text-white relative overflow-hidden" data-testid="partner-page">
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 20% 10%, #4338CA55, transparent), radial-gradient(40% 40% at 90% 80%, #05966955, transparent)" }} />
      <header className="relative h-20 flex items-center justify-between px-5 sm:px-10">
        <div className="flex items-center gap-2" data-testid="partner-fixed-brand">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center"><Compass className="w-5 h-5 text-white" /></div>
          <span className="font-heading font-extrabold text-lg">Motley Mentors <span className="text-white/40 font-medium">·</span> CareerCompass</span>
        </div>
        <div className="flex items-center gap-3" data-testid="partner-brand">
          <span className="font-body text-[10px] uppercase tracking-widest text-white/50 hidden sm:inline">in partnership with</span>
          {partner.logo && <img src={partner.logo} alt={partner.name} className="h-9 max-w-[140px] object-contain rounded bg-white/90 p-1" data-testid="partner-logo" />}
          <span className="font-heading font-bold" data-testid="partner-name">{partner.name}</span>
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-5 sm:px-10 py-10 lg:py-20 grid lg:grid-cols-5 gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-3">
          <p className="font-heading font-bold text-sm text-emerald-300 tracking-wide uppercase">{partner.name} × CareerCompass</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">
            Map your next career move <span className="text-indigo-300">with science, not guesswork.</span>
          </h1>
          <p className="font-body text-base md:text-lg text-white/70 mt-6 max-w-xl" data-testid="partner-tagline">
            {partner.tagline || "Two tracks, one science-backed assessment: CareerCompass Start for students choosing what to study and their first job, and CareerCompass Comeback for career changers and returners — each ending in a personalised career alignment report."}
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            {PILLARS.map(([Icon, t, d], i) => (
              <motion.div key={t} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4 flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-indigo-200" /></div>
                <div>
                  <p className="font-heading font-bold text-sm">{t}</p>
                  <p className="font-body text-xs text-white/60 mt-0.5">{d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-2 bg-white text-slate-900 rounded-3xl p-8 shadow-2xl shadow-indigo-900/40" data-testid="partner-access-card">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5"><KeyRound className="w-6 h-6 text-primary" /></div>
          <h2 className="font-heading font-extrabold text-2xl">Enter your access code</h2>
          <p className="font-body text-sm text-slate-500 mt-2">Use the code and password {partner.name} sent you. Already started? The same details resume your assessment.</p>
          <div className="mt-6"><AccessCodeForm ctaLabel="Begin my assessment" /></div>
          <p className="font-body text-xs text-slate-400 mt-6 text-center">No code yet? Ask {partner.name} for your invitation.</p>
        </motion.div>
      </main>
    </div>
  );
};

export default PartnerPage;
